import { PPN_IN_PERCENT } from "@marketplace/shared/constants";
import { formatIDR } from "@marketplace/shared/utils";
import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Modal,
  Text
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import clsx from "clsx";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { MdOutlineFileDownload } from "react-icons/md";

import { usePermissions } from "@/hooks";
import { httpClient } from "@/http";
import { formatWithLocale } from "@/utils";

import { orderDetailModalAtom } from "../../atoms/order-detail-atom";
import { useGetOrderDetailQuery } from "../../hooks/queries/use-get-order-detail-query";
import classes from "./order-detail-modal.module.css";
import { OrderItemsTable } from "./order-items-table";

type OrderDetailModalProps = {
  opened: boolean;
  onClose: () => void;
};

type OrderDetailDescriptionItemProps = {
  label: string;
  value?: string | React.ReactNode;
  wrapperClassName?: string;
  truncate?: boolean;
};

const formatDate = (date?: string) => {
  if (!date) return "-";

  const dateObj = new Date(date);
  return formatWithLocale(dateObj, "dd MMMM yyyy");
};

const OrderDetailDescriptionItem = ({
  label,
  value,
  wrapperClassName = "",
  truncate = true
}: OrderDetailDescriptionItemProps) => {
  return (
    <div className={wrapperClassName}>
      <Text className="mb-2 text-fjord-grey-500">{label}</Text>
      {typeof value === "string" ? (
        <Text
          title={value}
          className={clsx("text-fjord-grey-400", truncate && "truncate")}
          type="heading"
          size="xxs"
          weight="semibold"
        >
          {value || "-"}
        </Text>
      ) : (
        value
      )}
    </div>
  );
};

export const OrderDetailModal = ({
  opened,
  onClose
}: OrderDetailModalProps) => {
  const { t: tLabel } = useTranslation("order", {
    keyPrefix: "orderListPage.orderDetailModal.label"
  });
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage"
  });

  const orderId = useAtomValue(orderDetailModalAtom).data?.id;

  const { data, isLoading } = useGetOrderDetailQuery({
    queryParams: {
      orderId: orderId!
    },
    options: {
      enabled: opened && !!orderId
    }
  });
  const { isSuperAdmin } = usePermissions();

  const orderDetailData = data?.data ?? null;
  const hasInvoice = !!orderDetailData?.invoice_path;
  const hasInternalInvoice = !!orderDetailData?.invoice_path_internal;

  const handleViewInvoice = async (path?: string | null) => {
    if (!path) return;
    try {
      const response = await httpClient.get(`media/${path}`);

      if (!response.ok) {
        throw new Error("Failed to fetch invoice file");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error("Failed to open invoice file:", error);
      notifications.show({
        variant: "error",
        message: "Failed to open file"
      });
    }
  };

  const handleDownloadInvoice = () => {
    handleViewInvoice(orderDetailData?.invoice_path);
  };

  const handleDownloadInternalInvoice = () => {
    handleViewInvoice(orderDetailData?.invoice_path_internal);
  };

  return (
    <Modal.Root opened={opened} onClose={onClose} size={610}>
      <Modal.Overlay />
      <Modal.Content>
        <ConditionalRendering
          condition={!isLoading && (hasInvoice || hasInternalInvoice)}
        >
          <div className="flex justify-end gap-2 px-6 py-2 bg-mischka-100">
            <ConditionalRendering condition={hasInvoice}>
              <Button
                variant="secondary"
                size="sm"
                rightSection={<MdOutlineFileDownload size={14} />}
                onClick={handleDownloadInvoice}
              >
                {t("orderDetailModal.download")}
              </Button>
            </ConditionalRendering>
            <ConditionalRendering
              condition={hasInternalInvoice && isSuperAdmin}
            >
              <Button
                variant="secondary"
                size="sm"
                rightSection={<MdOutlineFileDownload size={14} />}
                onClick={handleDownloadInternalInvoice}
              >
                {t("orderDetailModal.downloadInternal")}
              </Button>
            </ConditionalRendering>
          </div>
        </ConditionalRendering>
        <Modal.Header className={classes.modalHeader}>
          <Modal.Title>
            <Text type="heading">{t("orderDetail")}</Text>
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body className={classes.modalBody}>
          <ConditionalRendering condition={isLoading}>
            <Center className="min-h-[450px]">
              <Loader />
            </Center>
          </ConditionalRendering>
          <ConditionalRendering condition={!isLoading}>
            <div className="grid grid-cols-2 gap-[14px]">
              <OrderDetailDescriptionItem
                label={tLabel("fullName")}
                value={orderDetailData?.customer?.name}
              />
              <OrderDetailDescriptionItem
                label={tLabel("date")}
                value={formatDate(orderDetailData?.order_created_at)}
              />
              <OrderDetailDescriptionItem
                label={tLabel("email")}
                value={orderDetailData?.customer?.email}
                wrapperClassName="col-span-2"
              />
              <hr className="bg-mischka-100 h-px border-0 w-full col-span-2" />
              <OrderDetailDescriptionItem
                label={tLabel("company")}
                value={
                  orderDetailData?.customer?.company_brand_name ||
                  orderDetailData?.customer?.company_name
                }
                truncate={false}
              />
              <OrderDetailDescriptionItem
                label={tLabel("siup")}
                value={orderDetailData?.customer?.nib}
              />
              <OrderDetailDescriptionItem
                label={tLabel("npwp")}
                value={orderDetailData?.customer?.npwp}
              />
            </div>

            <hr className="my-6 bg-mischka-100 h-px border-0 w-full" />

            <OrderItemsTable
              orderItems={orderDetailData?.order_items ?? []}
              t={t}
            />

            <div className="text-fjord-grey-500">
              <div className="mb-[14px] flex justify-between items-center">
                <Text size="s">{t("orderDetailModal.subTotal")}</Text>
                <Text size="s">
                  {formatIDR(orderDetailData?.sub_total as number)}
                </Text>
              </div>
              <ConditionalRendering
                condition={
                  orderDetailData?.voucher_amount &&
                  orderDetailData.voucher_amount > 0
                }
              >
                <div className="mb-[14px] flex justify-between items-center">
                  <Text size="s">{t("orderDetailModal.discount")}</Text>
                  <Text size="s">
                    - {formatIDR(orderDetailData?.voucher_amount ?? 0)}
                  </Text>
                </div>
              </ConditionalRendering>
              <hr className="my-4 border-0 h-px bg-mischka-100" />

              <div className="mb-[14px] flex justify-between items-center">
                <Text size="s">{t("orderDetailModal.dpp")}</Text>
                <Text size="s">
                  {formatIDR(orderDetailData?.dpp_amount ?? 0)}
                </Text>
              </div>
              <div className="mb-[14px] flex justify-between items-center">
                <Text size="s">
                  {t("orderDetailModal.ppn", { ppn: PPN_IN_PERCENT })}
                </Text>
                <Text size="s">
                  {formatIDR(orderDetailData?.ppn_amount ?? 0)}
                </Text>
              </div>

              <ConditionalRendering
                condition={(orderDetailData?.stamp_fee as number) > 0}
              >
                <div className="mb-[14px] flex justify-between items-center">
                  <Text size="s">{t("orderDetailModal.stampFee")}</Text>
                  <Text size="s">
                    {formatIDR(orderDetailData?.stamp_fee ?? 0)}
                  </Text>
                </div>
              </ConditionalRendering>
              <ConditionalRendering
                condition={orderDetailData?.transaction_fee}
              >
                <div className="mb-[14px] flex justify-between items-center">
                  <Text size="s">{t("orderDetailModal.transactionFee")}</Text>
                  <Text size="s">
                    {formatIDR(orderDetailData?.transaction_fee ?? 0)}
                  </Text>
                </div>
              </ConditionalRendering>
              <ConditionalRendering condition={orderDetailData?.platform_fee}>
                <div className="mb-[14px] flex justify-between items-center">
                  <Text size="s">{t("orderDetailModal.platformFee")}</Text>
                  <Text size="s">
                    {formatIDR(orderDetailData?.platform_fee ?? 0)}
                  </Text>
                </div>
              </ConditionalRendering>
            </div>

            <hr className="my-4 bg-mischka-100 h-px border-0 w-full" />

            <div className="flex justify-between items-center text-humble-blue-500">
              <Text type="heading" size="s">
                {t("orderDetailModal.total")}
              </Text>
              <Text type="heading" size="s">
                {formatIDR(orderDetailData?.grand_total ?? 0)}
              </Text>
            </div>
          </ConditionalRendering>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
